import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import { colors } from '../constants/colors';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import { fontSize, fontWeight } from '../constants/typography';
import { useLocation } from 'react-router-dom';
import { writeDefaultTabLabel, writeTabLabel } from '../hooks/useBreadcrumbs';

const ContentTabsDepthContext = React.createContext(0);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wrapped-tabpanel-${index}`}
      aria-labelledby={`wrapped-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={2}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function tabProps(index) {
  return {
    id: `wrapped-tab-${index}`,
    'aria-controls': `wrapped-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: '10px',
    overflow: 'visible',
  },
  tabs: {
    position: 'sticky',
    top: 0,
    zIndex: 4,
    backgroundColor: colors.white,
    '& .MuiTab-root': {
      color: colors.orange,
      textTransform: 'capitalize',
      fontSize: fontSize.tabLabel,    // 14px — from typography tokens
      fontWeight: fontWeight.medium,
      maxWidth: 'unset !important',
      minHeight: 40,
      padding: '6px 14px',
    },
    '& .MuiTabs-indicator': {
      backgroundColor: colors.orange,
    }
  }
}));

export default function ContentTabs(props) {
  const classes = useStyles();
  const depth = React.useContext(ContentTabsDepthContext);
  const [value, setValue] = React.useState(props.value);
  React.useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (props.onChange) {
      props.onChange(newValue)
    }
  };

  // Filter out hidden tabs before rendering them
  const visibleTabs = React.Children.toArray(props.children)?.filter(
    (c) => c?.props?.visible
  );

  const { pathname } = useLocation();
  const firstTabLabel = visibleTabs[0]?.props?.label;

  React.useLayoutEffect(() => {
    if (depth !== 0) return;
    for (const tab of visibleTabs) {
      const label = tab.props?.label;
      const index = tab.props?.index;
      if (label && index != null) writeTabLabel(pathname, String(index), label);
    }
    // Also fire the first-tab event so BreadcrumbTrailContext can patch "View" → "View (label)".
    if (firstTabLabel) writeDefaultTabLabel(pathname, firstTabLabel);
  }, [pathname, firstTabLabel, depth]); // firstTabLabel proxies for "tab structure changed"

  return (
    <ContentTabsDepthContext.Provider value={depth + 1}>
    <Paper className={classes.root}>
      <Tabs
        value={value}
        variant="scrollable"
        onChange={handleChange}
        className={`${classes.tabs} ccl-tabs`}
      >
        {visibleTabs.map((c, i) => (
          <Tab
            key={`tab-${i}`}
            label={c.props?.label}
            value={c.props?.index}
            className="ccl-tab-item"
            {...tabProps(c.props?.index)}
          />
        ))}
      </Tabs>

      {visibleTabs.map((c, i) => (
        <TabPanel key={`tabpanel-${i}`} value={value} index={c.props?.index}>
          {c.props?.children}
        </TabPanel>
      ))}
    </Paper>
    </ContentTabsDepthContext.Provider>
  );
}